import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useCampaign } from "@/context/CampaignContext";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Loader2, CheckCircle, EyeOff, Eye } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import {
  connectUserEmail,
  connectZohoProvider,
} from "../../Redux/ReduxSlices/EmailTemplateSlice";
import { checkDomain } from "../../Redux/ReduxSlices/analyticsDashboardSlice";
import Header from "../layout/Header";
import Footer from "../layout/Footer";
import { fetchUserDetails } from "../../Redux/ReduxSlices/profileSlice";
import {
  checkSMTPProviderEmail,
  saveSMTPConfig,
  verifySMTPConfig,
} from "../../Redux/ReduxSlices/smptProviderSlice";
import AppPasswordStepsModal from "./AppPasswordStepsModal";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import OAuthStepsModal from "./OAuthStepsModal";

export default function EmailConnectModal({
  open,
  onClose,
  onSuccess,
  asPage = false,
}) {
  const { isEmailConnected, isLoading, error } = useCampaign();
  const [connectInProgress, setConnectInProgress] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [verifySmtpLoading, setVerifySmtpLoading] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { details: userData } = useSelector((state) => state.profile);
  const { saveData } = useSelector((state) => state.smptProvider);

  const [domainInfo, setDomainInfo] = useState(null);
  const [isCustomProvider, setIsCustomProvider] = useState(false);

  const [verifySmtpOpen, setVerifySmtpOpen] = useState(false);
  const [appPasswordModal, setAppPasswordModal] = useState(false);
  const [OAuthModal, setOAuthModal] = useState(false);
  const [activeConnection, setActiveConnection] = useState(null);

  const [smtpConfig, setSmtpConfig] = useState({
    host: "",
    port: "",
    security: "",
  });

  const userId = userData?.data?.id;

  const form = useForm({
    defaultValues: {
      email: "",
      username: "",
      password: "",
    },
  });

  useEffect(() => {
    dispatch(fetchUserDetails());
  }, [dispatch]);

  const isMobile = () =>
    /Android|iPhone|iPad|iPod|Opera Mini|IEMobile/i.test(navigator.userAgent);

  const watchedEmail = form.watch("email");
  useEffect(() => {
    setDomainInfo(null);
    setIsCustomProvider(false);
    form.setValue("username", "");
    form.setValue("password", "");
    setShowPassword(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedEmail]);

  const saveCustomSMTP = async ({ email, username, password }) => {
    const fromName =
      userData?.data?.full_name ||
      userData?.data?.name ||
      email?.split("@")?.[0] ||
      "User";

    const payload = {
      from_email: email,
      from_name: fromName,
      username: username || email,
      host: smtpConfig.host,
      port: smtpConfig.port,
      security: smtpConfig.security,
      app_password: password,
      imap_password: password,
      user_id: userId,
    };

    const saved = await dispatch(saveSMTPConfig(payload)).unwrap();

    if (!saved?.ok) {
      toast.error("Authentication failed. Username or password is incorrect.");
      return false;
    }

    toast.success("SMTP settings saved. Please verify.");
    setVerifySmtpOpen(true);
    return true;
  };

  const handleVerifySMTP = async () => {
    setVerifySmtpLoading(true);
    try {
      const res = await dispatch(
        verifySMTPConfig({
          user_id: userId,
          config_id: saveData?.id,
        }),
      ).unwrap();

      if (res?.ok) {
        toast.success(res?.message || "SMTP verified successfully!");

        form.reset({
          email: "",
          username: "",
          password: "",
        });

        setSmtpConfig({
          host: "",
          port: "",
          security: "",
        });

        setIsCustomProvider(false);
        setDomainInfo(null);
        setShowPassword(false);
        setActiveConnection(null);
        setVerifySmtpOpen(false);
        setConnectInProgress(false);

        onClose?.();

        setTimeout(() => {
          navigate("/send-emails");
        }, 1200);
        setVerifySmtpLoading(false);
        return;
      }
      setVerifySmtpLoading(false);
      toast.error(res?.message || "SMTP verification failed.");
    } catch (err) {
      setVerifySmtpLoading(false);
      console.error("Verify SMTP error:", err);
      toast.error("SMTP verification failed.");
    }
  };

  // Add this effect to listen for OAuth completion
  useEffect(() => {
    // Handle popup message (desktop)
    const handleMessage = (event) => {
      if (event.origin !== window.location.origin) return;

      if (event.data?.type === "MAXIPITCH_EMAIL_OAUTH_SUCCESS") {
        // Close popup if still open
        if (window.maxiPitchOAuthPopup && !window.maxiPitchOAuthPopup.closed) {
          window.maxiPitchOAuthPopup.close();
        }

        // Clear state
        setConnectInProgress(false);
        setActiveConnection(null);

        // Optional: no full reload (better UX)
        toast.success("Email connected successfully!");

        // Trigger callback
        if (onSuccess) onSuccess();

        // Close modal
        onClose?.();

        // Redirect instead of reload (recommended)
        setTimeout(() => {
          navigate("/send-emails");
        }, 800);
      }
    };

    // Handle new tab / mobile fallback
    const handleStorage = (event) => {
      if (event.key === "maxipitch_email_oauth_success") {
        try {
          const data = JSON.parse(event.newValue || "{}");

          if (data?.type === "MAXIPITCH_EMAIL_OAUTH_SUCCESS") {
            localStorage.removeItem("maxipitch_email_oauth_success");

            setConnectInProgress(false);
            setActiveConnection(null);

            toast.success("Email connected successfully!");

            if (onSuccess) onSuccess();
            onClose?.();

            setTimeout(() => {
              navigate("/send-emails");
            }, 800);
          }
        } catch (err) {
          console.error("Storage parse error:", err);
        }
      }
    };

    window.addEventListener("message", handleMessage);
    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener("message", handleMessage);
      window.removeEventListener("storage", handleStorage);
    };
  }, [onSuccess, onClose, navigate]);

  const openOAuthPopup = (authUrl) => {
    const width = 600;
    const height = 700;
    const left = window.screenX + Math.max(0, (window.outerWidth - width) / 2);
    const top = window.screenY + Math.max(0, (window.outerHeight - height) / 2);

    const popup = window.open(
      authUrl,
      "OAuthPopup",
      `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`,
    );

    if (!popup) {
      // Popup blocked → fallback to redirect
      window.location.href = authUrl;
      return;
    }

    window.maxiPitchOAuthPopup = popup;
    popup.focus();

    // Detect manual close
    const checkPopupClosed = setInterval(() => {
      if (popup.closed) {
        clearInterval(checkPopupClosed);

        setConnectInProgress(false);
        setActiveConnection(null);
      }
    }, 1000);
  };
  const handleConnect = async (data) => {
    if (activeConnection === "smtp") return;

    setActiveConnection("oauth");
    setConnectInProgress(true);

    try {
      const domainRes = await dispatch(checkDomain(data.email)).unwrap();
      setDomainInfo(domainRes);

      const detectedProvider = domainRes?.provider || "";
      const normalized = (detectedProvider || "").toLowerCase();

      if (normalized === "yahoo") {
        toast.error(
          "Yahoo email addresses are not supported. Please use another provider",
        );
        setActiveConnection("");
        form.reset({ email: "", username: "", password: "" });
        return;
      }

      if (normalized === "custom") {
        setIsCustomProvider(true);

        try {
          const smtpRes = await dispatch(
            checkSMTPProviderEmail({
              user_id: userId,
              from_email: data.email,
            }),
          ).unwrap();

          const guess = smtpRes?.guess || {};

          setSmtpConfig({
            host: guess?.host || "",
            port: guess?.port || "",
            security: guess?.security || "",
          });
        } catch (err) {
          toast.error("Unable to detect SMTP settings automatically.");
        }

        form.setValue("username", data.email);
        form.setValue("password", "");

        toast.info("Custom domain detected. Please enter SMTP credentials.");
        return;
      }

      let res;
      if (normalized === "zoho") {
        res = await dispatch(
          connectZohoProvider({
            provider: detectedProvider,
            email: data.email,
            userId,
          }),
        ).unwrap();
      } else {
        res = await dispatch(
          connectUserEmail({
            provider: detectedProvider,
            email: data.email,
            userId,
          }),
        ).unwrap();
      }

      const authUrl = res?.auth_url || res?.url;
      if (!authUrl) throw new Error("Auth URL missing");

      onClose?.();
      form.reset({ email: "", username: "", password: "" });

      if (isMobile()) {
        window.location.href = authUrl;
        return;
      }

      openOAuthPopup(authUrl);
    } catch (err) {
      console.error("Email connect error:", err);
      toast.error(err?.message || "Email connection failed.");
      onClose?.();
      form.reset({ email: "", username: "", password: "" });
    } finally {
      setConnectInProgress(false);
    }
  };

  const handleCustomSubmit = async (data) => {
    setActiveConnection("smtp");
    setConnectInProgress(true);

    try {
      if (!data.email) {
        toast.warning("Please enter email");
        return;
      }
      if (!data.username) {
        toast.warning("Please enter username");
        return;
      }
      if (!data.password) {
        toast.warning("Please enter password");
        return;
      }

      await saveCustomSMTP({
        email: data.email,
        username: data.username,
        password: data.password,
      });
    } catch (err) {
      console.error("Custom SMTP error:", err);
      toast.error(err?.message || "Failed to save SMTP settings.");
    } finally {
      setConnectInProgress(false);
    }
  };

  const handleClose = () => {
    form.reset({ email: "", username: "", password: "" });
    setShowPassword(false);
    setActiveConnection(null);
    setVerifySmtpOpen(false);
    setAppPasswordModal(false);
    setIsCustomProvider(false);
    setDomainInfo(null);
    onClose?.();
  };

  const formContent = (
    <div>
      {isEmailConnected ? (
        <div className="flex items-center justify-center flex-col text-center space-y-2 p-6">
          <CheckCircle className="h-12 w-12 text-green-500" />
          <h3 className="font-medium text-xl">Successfully Connected</h3>
          <p className="text-sm text-muted-foreground">
            Your email account has been connected successfully.
          </p>
        </div>
      ) : (
        <div>
          <div className="rounded-lg py-2 px-4 pb-4">
            <p className="text-sm text-muted-foreground mb-4">
              Use <span className="font-medium">Gmail</span>,{" "}
              <span className="font-medium pr-0.5">Zoho</span>or{" "}
              <span className="font-medium pr-1">Outlook</span>or{" "}
              <span className="font-medium pr-1">SMTP</span>to connect your
              email account.
            </p>

            <p className="text-sm text-rose-600 font-semibold mb-4">
              Note: Please enter the email address that will be used to send all
              emails in this campaign
            </p>

            {domainInfo?.provider?.toLowerCase() === "custom" && (
              <div className="mb-4 p-3 rounded-md border border-yellow-200 bg-yellow-50 text-yellow-900 text-sm">
                Custom domain detected (<b>{domainInfo.domain}</b>). Please
                provide email username and password.
              </div>
            )}

            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(
                  isCustomProvider ? handleCustomSubmit : handleConnect,
                )}
                className="space-y-4"
              >
                <FormField
                  control={form.control}
                  name="email"
                  rules={{
                    required: "Email address is required",
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: "Invalid email address",
                    },
                  }}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email Address</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="your.email@example.com"
                          type="email"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {isCustomProvider && (
                  <>
                    <div className="mt-4 px-3 py-2 border border-indigo-200 rounded-md bg-blue-50 text-sm max-md:text-xs">
                      <div className="flex flex-wrap items-center gap-x-8 gap-y-1 text-indigo-800">
                        <span>
                          <span className="font-medium text-indigo-700">
                            Host:
                          </span>{" "}
                          <span className="text-gray-800 break-all">
                            {smtpConfig.host || "-"}
                          </span>
                        </span>

                        <span>
                          <span className="font-medium text-indigo-700">
                            Port:
                          </span>{" "}
                          <span className="text-gray-800">
                            {smtpConfig.port || "-"}
                          </span>
                        </span>

                        <span>
                          <span className="font-medium text-indigo-700">
                            Security:
                          </span>{" "}
                          <span className="text-gray-800 uppercase">
                            {smtpConfig.security || "-"}
                          </span>
                        </span>
                      </div>
                    </div>

                    <FormField
                      control={form.control}
                      name="username"
                      rules={{ required: "Username is required" }}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Username</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="Usually same as email"
                              type="text"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="password"
                      rules={{ required: "Password is required" }}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Password</FormLabel>
                          <FormControl>
                            <div className="relative">
                              <Input
                                placeholder="Enter password"
                                type={showPassword ? "text" : "password"}
                                {...field}
                              />
                              <button
                                type="button"
                                onClick={() => setShowPassword((v) => !v)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
                              >
                                {showPassword ? (
                                  <EyeOff className="h-4 w-4" />
                                ) : (
                                  <Eye className="h-4 w-4" />
                                )}
                              </button>
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </>
                )}

                <Button
                  type="submit"
                  className="w-full main-button"
                  disabled={connectInProgress || isLoading}
                >
                  {connectInProgress ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      {isCustomProvider ? "Saving..." : "Connecting..."}
                    </>
                  ) : isCustomProvider ? (
                    "Save & Verify"
                  ) : (
                    "Connect Now"
                  )}
                </Button>
              </form>
            </Form>
          </div>
        </div>
      )}

      <Dialog open={verifySmtpOpen} onOpenChange={setVerifySmtpOpen}>
        <DialogContent className="w-[calc(100vw-2rem)] max-w-md p-4 sm:p-6">
          <DialogDescription>
            <div />
          </DialogDescription>
          <DialogTitle>Verify your SMTP</DialogTitle>
          <DialogFooter className="flex flex-col-reverse sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setVerifySmtpOpen(false)} disabled={verifySmtpLoading}>
              Close
            </Button>
            <Button onClick={handleVerifySMTP} className="main-button" disabled={verifySmtpLoading}>
              {verifySmtpLoading?"Verifying...":"Verify"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {error && <p className="text-red-500 text-sm">{error}</p>}

      <AppPasswordStepsModal
        open={appPasswordModal}
        onClose={() => setAppPasswordModal(false)}
      />
      <OAuthStepsModal open={OAuthModal} onClose={() => setOAuthModal(false)} />
    </div>
  );

  if (asPage) {
    return (
      <div className="flex flex-col min-h-screen bg-gray-50">
        <Header />

        <main className="flex-grow container mx-auto max-md:px-4 max-md:w-full px-4">
          <div className="w-full max-w-xl flex">
            <div className="pt-6">
              <h2 className="text-2xl font-semibold mb-2">
                Connect Email Provider
              </h2>
            </div>

            <button
              className="text-blue-600 font-medium text-sm hover:underline px-6 -mt-12"
              onClick={() => setOAuthModal(true)}
            >
              Help?
            </button>
          </div>

          <div className="max-w-xl">{formContent}</div>
        </main>

        <Footer />
      </div>
    );
  }

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <DialogContent
        className="
          w-[calc(100vw-2rem)]
          sm:max-w-[600px]
          p-0
          backdrop-blur-sm
          rounded-2xl
        "
      >
        <div className="flex flex-col max-h-[85vh]">
          <div className="p-5 pb-3 flex items-start justify-between">
            <div className="px-4">
              <DialogTitle className="text-lg font-semibold">
                Connect Your Email
              </DialogTitle>
            </div>

            <button
              className="text-blue-600 font-medium text-sm hover:underline"
              onClick={() => setOAuthModal(true)}
            >
              Help?
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-5">{formContent}</div>

          <div className="p-5 pt-3 border-t">
            <DialogFooter className="flex flex-col-reverse sm:flex-row gap-2" />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
